import { Badge } from '@solinkcorp/solink-ui';
import { type FC } from 'react';
import { type Investigation } from 'src/hooks/investigations/useInvestigations';
import useLocale from 'src/hooks/useLocale';
import LocaleUtil from 'utils/LocaleUtil';

interface InvestigationCountBadgesProps {
  investigation: Investigation;
  className?: string;
}

/**
 * Event / Clip count badges shared by the investigations list card and the
 * add-to-investigation picker so the two stay visually in sync.
 */
export const InvestigationCountBadges: FC<InvestigationCountBadgesProps> = ({
  investigation,
  className = 'tw mt-3 flex items-center gap-2',
}) => {
  const t = useLocale().investigations.card;
  const eventCount = investigation.events.filter((e) => e.kind === 'event').length;
  const clipCount = investigation.events.filter((e) => e.kind === 'clip').length;

  if (eventCount === 0 && clipCount === 0) return null;

  const eventBadge =
    eventCount === 1
      ? t.eventCountSingular
      : LocaleUtil.getLocalizedStr({
          string: t.eventCountPlural,
          replacements: { count: eventCount },
        });
  const clipBadge =
    clipCount === 1
      ? t.clipCountSingular
      : LocaleUtil.getLocalizedStr({
          string: t.clipCountPlural,
          replacements: { count: clipCount },
        });

  return (
    <div className={className}>
      {eventCount > 0 && (
        <Badge variant="secondary" className="tw rounded px-2 py-1">
          {eventBadge}
        </Badge>
      )}
      {clipCount > 0 && (
        <Badge variant="secondary" className="tw rounded px-2 py-1">
          {clipBadge}
        </Badge>
      )}
    </div>
  );
};
